"use client";

import React from "react";
import { Search, DoorOpen, Wrench, ShieldCheck, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const steps = [{
  id: 1,
  title: "Recolección de Materia Prima",
  description: "Recuperamos aceites de cocina usados y biomasa local en toda Cuba, evitando que contaminen nuestras fuentes de agua y suelos.",
  icon: Search,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689054/img/recoleccion_aceite_biomasa.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689054/img/recoleccion_aceite_biomasa.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689054/img/recoleccion_aceite_biomasa.png-wvc-sizes",
    alt: "Recolección de aceites vegetales usados para biodiesel"
  }
}, {
  id: 2,
  title: "Proceso de Transesterificación",
  description: "Mediante una reacción química controlada, transformamos los lípidos en ésteres metílicos, separando la glicerina del combustible limpio.",
  icon: DoorOpen,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689054/img/transesterificacion_quimica.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689054/img/transesterificacion_quimica.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689054/img/transesterificacion_quimica.png-wvc-sizes",
    alt: "Reacción química de transesterificación en laboratorio"
  }
}, {
  id: 3,
  title: "Purificación y Filtrado",
  description: "Eliminamos impurezas y residuos de humedad para asegurar un Biodiesel de alta calidad que cumple con los estándares internacionales de motores.",
  icon: Wrench,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689054/img/purificacion_biodiesel_final.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689054/img/purificacion_biodiesel_final.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689054/img/purificacion_biodiesel_final.png-wvc-sizes",
    alt: "Sistema de filtrado y purificación de combustible"
  }
}, {
  id: 4,
  title: "Distribución de Energía Limpia",
  description: "El producto final se entrega para su uso en transporte y maquinaria, reduciendo drásticamente las emisiones de CO2 en nuestra isla.",
  icon: ShieldCheck,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689054/img/distribucion_energia_renovable.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689054/img/distribucion_energia_renovable.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689054/img/distribucion_energia_renovable.png-wvc-sizes",
    alt: "Suministro de biodiesel BioPower"
  }
}];
export default function Biodieselproductionprocess() {
  return <section data-section-id="259666" id="biodieselproductionprocess" className="py-16 md:py-24 bg-background text-foreground overflow-hidden">
      <div id="biodiesel-process" className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge variant="secondary" className="mb-4 bg-secondary/10 text-secondary border-secondary/20 text-xs uppercase tracking-widest">
            Proceso de Producción Animado
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default text-foreground mb-6 tracking-tight">
            Cómo Creamos Energía en BioPower
          </h2>
          <p className="text-base text-muted-foreground font-default">
            Transformamos residuos en recursos. Nuestra metodología asegura un ciclo de energía circular que reduce la dependencia del petróleo en Cuba.
          </p>
        </div>

        <div className="relative max-w-5xl mx-auto">
          {}
          <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary via-secondary to-chart-3 md:-translate-x-1/2 rounded-lg opacity-30" />

          <div className="space-y-12 md:space-y-24">
            {steps.map((step, index) => {
            const isEven = index % 2 === 0;
            const Icon = step.icon;
            return <div key={step.id} data-index={index} className={`relative flex flex-col md:flex-row items-start md:items-center ${isEven ? "md:flex-row-reverse" : ""}`}>
                  {}
                  <div className="absolute left-6 md:left-1/2 top-8 md:top-1/2 w-12 h-12 bg-primary text-primary-foreground rounded-lg flex items-center justify-center -translate-x-1/2 md:-translate-y-1/2 border-4 border-background shadow-lg z-10 shrink-0 transition-transform hover:scale-110">
                    <Icon className="w-5 h-5" />
                  </div>

                  {}
                  <div className={`w-full pl-16 md:pl-0 md:w-1/2 ${isEven ? "md:pr-16 lg:pr-24" : "md:pl-16 lg:pl-24"}`}>
                    <Card className="overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border-border/40 bg-card/50 backdrop-blur-sm rounded-lg">
                      <div className="relative h-48 sm:h-56 overflow-hidden bg-muted rounded-t-lg hover:scale-110 transition-transform duration-500">
                        <img src={step.image.src} data-wvc-srcset={step.image.srcset} data-wvc-sizes={step.image.sizes} alt={step.image.alt} className="w-full h-full object-cover transition-transform duration-700 hover:scale-110" loading="lazy" />
                        <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent" />
                      </div>
                      <CardHeader className="pb-4">
                        <Badge className="w-fit mb-3 bg-accent text-accent-foreground hover:bg-accent/80 font-default text-xs uppercase tracking-widest">
                          Etapa {step.id}
                        </Badge>
                        <CardTitle className="text-xl md:text-2xl font-default text-foreground leading-tight">
                          {step.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-muted-foreground font-default leading-relaxed">
                          {step.description}
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                </div>;
          })}
          </div>
        </div>

        <div className="mt-20 text-center">
          <Button size="lg" className="rounded-md px-8 bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg transition-all hover:translate-y-[-2px]" asChild>
            <Link to="/HomePage">
              Descubre el Impacto del Biodiesel
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}